import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { GoogleSheetsIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { GoogleAccount } from '@mm/state/interfaces/googleSheet';
import {
  ConnectionKind,
  FormattedConnectionModel,
} from '@mm/state/model/ConnectionModel';
import { useConnectionsTableActions } from '@mm/state/modules/connectionsTableState/actions';
import { GoogleSheetsConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { selectGoogleAccounts } from '@mm/state/selectors/googleSheetSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { OAuthPopupActionButton } from '@mm/stateful-components/OAuth/OAuthPopupActionButton';
import { ConfigProvider, Table } from 'antd';
import { ColumnType } from 'antd/es/table/interface';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { IntegrationErrors } from '../../../constants';
import { ReactComponent as AddGoogleAccountIcon } from '../../../icons/add-google-sheets.svg';
import '../../IntegrationBaseComponent.scss';
import { GoogleSheetsHeader } from './GoogleSheetsHeader';

type Props = {
  isAccountConnected: boolean;
  onDisconnect: (id: number) => void;
  spreadsheetCount: number;
  columns: ColumnType<FormattedConnectionModel>[];
  googleSheetsConnections: GoogleSheetsConnections[];
  name?: string;
};

export const GoogleSheets = (props: Props) => {
  const {
    isAccountConnected,
    onDisconnect,
    spreadsheetCount,
    columns,
    googleSheetsConnections,
    name,
  } = props;

  const accounts = useSelector(selectGoogleAccounts);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const { getOauthUrl, getAccounts } = useGoogleSheetsCRUDActions();
  const { setActiveIntegration } = useConnectionsTableActions();
  const { create } = useConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const addNewConnection = async (googleAccount?: GoogleAccount) => {
    const connection = await create(facebookPageId, {
      name: 'Connection',
      description: '',
      kind: ConnectionKind.WIDGET,
      connectionKind: 'google_spreadsheet',
      ...(googleAccount && !googleAccount.readOnly
        ? {
            googleAccountId: googleAccount.id,
            googleAccountEmail: googleAccount.email,
          }
        : {}),
    });

    if (connection) {
      showSlideoutMenu('googleSheetIntegrationEditor', {
        widgetId: connection.id,
      });
    }
  };

  const onClickAddNew = (updatedGoogleAccounts?: GoogleAccount[]) => {
    const sortedAccounts = updatedGoogleAccounts?.sort((a, b) =>
      (a.updatedAt || '').localeCompare(b.updatedAt || ''),
    );

    addNewConnection(sortedAccounts?.[0]);
    setActiveIntegration(name);
  };
  const [haveErrors, setHaveErrors] = useState(false);

  useEffect(() => {
    const errorsString = googleSheetsConnections
      .map(connection =>
        connection.connections.map(webhook =>
          webhook.webhookErrors?.map(error => error.errorDetails),
        ),
      )
      .flat()
      .join('');
    setHaveErrors(
      IntegrationErrors.some(error =>
        errorsString.toLowerCase().includes(error),
      ),
    );
  }, [googleSheetsConnections]);

  const headerComponent = (
    <GoogleSheetsHeader
      isAccountConnected={isAccountConnected}
      spreadsheetCount={spreadsheetCount}
      onClickAddNew={onClickAddNew}
    />
  );

  const sectionProps = {
    landmineName: landmineNames.INTEGRATIONS_NATIVE_INTEGRATIONS,
    featureFlag: 'nativeIntegrations',
  };

  return (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="Google Sheets"
          subtitle={`Automatically send customers data from ${companyName} to Google Sheets`}
          icon={<GoogleSheetsIcon />}
          headerRightComponent={headerComponent}
          css={{ icon: 'integrations__header-icon-googlesheets' }}
          haveErrors={haveErrors}
          {...sectionProps}
        />
      }
      integrationType="google_spreadsheet"
      emptyStateConfig={{
        button: accounts.length ? (
          'Add a Google Sheets Connection'
        ) : (
          <OAuthPopupActionButton
            fetchOauthPopupUrl={() => getOauthUrl('post_message')}
            oauthSuccessMessageType="googleAccountConnected"
            onRefreshNeeded={async () => {
              const accounts = await getAccounts(facebookPageId);
              if (accounts?.length) {
                onClickAddNew(accounts);
              }
            }}
            spinnerProps={{ size: 16, width: 32 }}
            buttonProps={{
              css: { container: 'integrations__empty-state-button' },
            }}
            {...sectionProps}
          >
            Add a Google Sheets Account
          </OAuthPopupActionButton>
        ),
        onClick: () => {
          onClickAddNew();
        },
        icon: <AddGoogleAccountIcon />,
        ...sectionProps,
      }}
      showEmptyState={!googleSheetsConnections.length || !accounts.length}
    >
      {googleSheetsConnections
        .filter(c => c.connections.length || c.accountEmail)
        .map((connection, connectionIdx) => (
          <div key={`google-connection-${connection.accountId}`}>
            <div className="integrations-grid__account-header">
              <div className="integrations-grid__account-info">
                Google Account:{' '}
                <span className="integrations-grid___account-name">
                  {connection.accountEmail
                    ? connection.accountEmail
                    : 'Not Assigned'}{' '}
                </span>
              </div>
              {accounts.find(
                acc => acc.id === connection.accountId && !acc.readOnly,
              ) && (
                <PrimaryButton
                  className="integrations__header-add-button-secondary"
                  onClick={() =>
                    onDisconnect(
                      Number(
                        accounts.find(acc => acc.id === connection.accountId)
                          ?.id,
                      ),
                    )
                  }
                  color="secondary-blue"
                >
                  Disconnect
                </PrimaryButton>
              )}
            </div>
            {!!connection.connections.length && (
              <ConfigProvider
                theme={{
                  components: {
                    Table: {
                      headerBg: 'white',
                      borderColor: '#D9D9D9',
                      cellPaddingBlockSM: 3,
                    },
                  },
                }}
              >
                <div className="integration-panel--ant-table">
                  <Table
                    dataSource={connection.connections}
                    columns={columns}
                    pagination={false}
                    size="small"
                    bordered
                  />
                </div>
              </ConfigProvider>
            )}
          </div>
        ))}
    </BaseIntegration>
  );
};
